/* eslint-disable complexity */

import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { trackPromise } from 'react-promise-tracker';
import { useLocation, useNavigate } from 'react-router-dom';

import { createContext, getWithContextHOC } from '@futrli/shared-services';
import { Forecast } from '@futrli-sdk/predict';
import { DATASOURCES_STATUS, ROUTES, useIsMounted } from '@futrli/shared-services';
import { LOADER_AREAS } from '@futrli/ui-components';
import { ConsolidatedForecastUpdateRequest } from '@futrli-sdk/consolidations';
import { clearForecastsQueryCache } from '@futrli/views/queries/forecasts';

import * as api from '../../../../core/api/forecast';
import { ConsolidatedForecastReport, Page, QueryParams } from '../../../../core/types';
import { detailSettingsPersistor } from '../detail-settings-persistor';
import { PeriodType } from '../../../../core/constants';
import { usePredictManagementContext } from '../../../../predict-management/context/predict-management-context';

import {
  generateQueryParams,
  getForecastGraphDate,
  getForecastIdFromURL,
  getForecastReportColumns,
  getOrganisationLastSync,
  getTotalPastDates
} from './utils';
import { ReportData } from './types';
import { useTotalCashPredictRow } from '../../predictions/hooks/useTotalCashPredictRow/useTotalCashPredictRow';
import { TotalRow } from '../../reports/data-grid/types';

export interface IDetailContext {
  deleteForecast: (isOnboarding?: boolean) => Promise<void>;
  fetchForecast: (queryParams?: QueryParams, forecastIdParam?: string, isOnboarding?: boolean) => Promise<void>;
  fetchForecastReport: (
    page?: Page,
    periodType?: PeriodType,
    forecastIdParam?: string,
    isOnboarding?: boolean
  ) => Promise<void>;
  forecast: Forecast | null;
  forecastGraphDate: string;
  forecastReportV2: ConsolidatedForecastReport | null;
  isFetchingReport?: boolean;
  isInitialDataLoading?: boolean;
  periodType: PeriodType;
  setPeriodType: (periodType: PeriodType) => void;
  syncForecast: (forecast: Forecast) => void;
  thisNumberExplained: Awaited<ReturnType<typeof api.fetchForecastThisNumberExplained>>['rows'];
  totalCashPredictRow: TotalRow | null;
  totalPastDates?: number;
  updateForecast: (
    forecast: Partial<Forecast>,
    shouldRefetchReport?: boolean,
    loadingArea?: LOADER_AREAS
  ) => Promise<void>;
}

/**
 * ProductManagement context initilised to be shared.
 *
 * NOTE: The context is comprise of all properties we want to share between the parent
 * and lower level nodes and the nodes between them do not need to know about these properties.
 */
export const DetailContext = createContext<IDetailContext>(({} as unknown) as IDetailContext);

const NON_FORECAST_DETAIL_ROUTE_SEGMENTS = new Set(
  [ROUTES.FORECASTS_LIST, ROUTES.PERFORMANCE_COMPARISON, ROUTES.ONBOARDING_ROUTE].map(route => route.slice(1))
);

// const isInitialMonthlyRequest = (page: Page | undefined, periodType: PeriodType) => {
//   return !page && periodType === PERIOD_TYPE_OPTIONS.MONTHLY;
// };

interface Props {
  workspaceId: string;
  children?: ReactNode;
}

export const DetailContextProvider = ({ workspaceId, children }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMounted = useIsMounted();
  const { addErrorMessage, financialConfig, organisation } = usePredictManagementContext();
  const forecastIdFromURL = getForecastIdFromURL();

  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [isFetchingReport, setIsFetchingReport] = useState<boolean>(false);
  const [settledForecastId, setSettledForecastId] = useState<string>();

  const lastLoadedForecastId = useRef('');
  const forecastRequestSequence = useRef(0);
  const reportRequestSequence = useRef(0);

  const [reportData, setReportData] = useState<ReportData>({
    forecastReportV2: null,
    periodType: detailSettingsPersistor.instance.periodType,
    thisNumberExplained: {}
  });

  const isForecastDetailsRoute = !!forecastIdFromURL && !NON_FORECAST_DETAIL_ROUTE_SEGMENTS.has(forecastIdFromURL);
  // A route change needs the full loader until that forecast settles; same-forecast refreshes keep the grid visible.
  const isInitialDataLoading = isForecastDetailsRoute && settledForecastId !== forecastIdFromURL;

  const forecastGraphDate = useMemo(() => {
    return getForecastGraphDate(forecast, organisation);
  }, [organisation, forecast]);

  const totalPastDates = useMemo(() => {
    if (forecast && organisation) {
      const columns = getForecastReportColumns(reportData);
      const forecastFromDate =
        forecast.kind === 'live' ? getOrganisationLastSync(organisation) : forecast.forecast_from_date;

      return getTotalPastDates(columns, reportData.periodType, financialConfig, forecastFromDate);
    }
  }, [financialConfig, organisation, forecast, reportData]);

  const reportCashPosition = reportData.forecastReportV2?.data.chart_items?.cash_position;
  const reportHasActuals = (reportCashPosition?.forecast_start_index ?? 0) > 0;

  const { totalCashPredictRow } = useTotalCashPredictRow(
    workspaceId,
    { id: organisation?.id || '', isConsolidation: false },
    forecast,
    reportData.forecastReportV2,
    // Only show previous actuals on the invoices tab, and only when the report actually includes them.
    // Otherwise the daily cash-flow window starts before the report's data and the chart labels drift.
    location.pathname.includes(ROUTES.INVOICES) && reportHasActuals
  );

  // *************
  // * Functions *
  // *************

  const deleteForecast = useCallback(
    async (isOnboarding?: boolean) => {
      if (!forecast || !organisation) {
        return;
      }

      try {
        await api.deleteForecast(workspaceId, organisation.id, forecast.id);
        if (isMounted.current) {
          setForecast(null);
          setIsFetchingReport(false);
          setReportData({
            forecastReportV2: null,
            periodType: detailSettingsPersistor.instance.periodType,
            thisNumberExplained: {}
          });
        }

        if (isOnboarding) {
          return;
        }

        navigate(
          `${ROUTES.WORKSPACES_ROUTE}/${workspaceId}${ROUTES.ORGANISATIONS_ROUTE}/${organisation.id}${ROUTES.PREDICT}${ROUTES.FORECASTS}`
        );
      } catch {
        addErrorMessage?.(new Error('Failed to delete forecast'));
      }
    },
    [addErrorMessage, forecast, navigate, isMounted, organisation, workspaceId]
  );

  /**
   * Fetches the forecasts by forecastId
   * @name fetchForecast
   * @param {QueryParams} queryParams
   * @todo manage errors
   */
  const fetchForecast = useCallback(
    async (queryParams?: QueryParams, forecastIdParam?: string): Promise<void> => {
      if (organisation) {
        // const forecastId = getParamFromRoute(match, ROUTE_PARAMS.FORECAST_ID) as string;
        let forecastId = getForecastIdFromURL();
        if (forecastId === 'onboarding') {
          forecastId = undefined;
        }
        const requestedForecastId = forecastId || forecastIdParam;
        forecastRequestSequence.current += 1;
        const requestSequence = forecastRequestSequence.current;
        try {
          let forecast = null;
          if (requestedForecastId) {
            lastLoadedForecastId.current = requestedForecastId;
            forecast = await api.fetchForecastById(
              workspaceId,
              organisation.id,
              requestedForecastId,
              queryParams
            );
          }

          if (isMounted.current && requestSequence === forecastRequestSequence.current) {
            setForecast(forecast);
          }
        } catch (e) {
          if (isMounted.current && requestSequence === forecastRequestSequence.current) {
            addErrorMessage?.(new Error('Failed to get forecast'));
            setSettledForecastId(requestedForecastId);
          }
        }
      }
    },
    [addErrorMessage, isMounted, organisation, workspaceId]
  );

  /**
   * Fetches the forecast report by forecastId
   * @name fetchForecastReport
   * @param {QueryParams} queryParams
   * @todo manage errors
   */
  const fetchForecastReport = useCallback(
    async (
      page?: Page,
      newPeriodType?: PeriodType,
      forecastIdParam?: string,
      isOnboarding?: boolean
    ): Promise<void> => {
      let forecastIdURL = getForecastIdFromURL();
      if (forecastIdURL === 'onboarding') {
        forecastIdURL = undefined;
      }
      const periodType = newPeriodType || reportData.periodType;

      if (!forecast || !organisation) {
        return;
      }

      const isSyncStarting = [
        DATASOURCES_STATUS.connect_success,
        DATASOURCES_STATUS.sync_start,
        DATASOURCES_STATUS.sync_initial_start
      ].includes(organisation.datasource.status.value as DATASOURCES_STATUS);

      const isSyncInProgress = [
        DATASOURCES_STATUS.sync_update_status,
        DATASOURCES_STATUS.sync_initial_update_status
      ].includes(organisation.datasource.status.value as DATASOURCES_STATUS);

      let forecastId;
      if (isOnboarding) {
        forecastId = forecast.id;
      } else {
        forecastId = forecastIdParam ? forecastIdParam : forecastIdURL ? forecastIdURL : forecast.id;
      }

      if (isMounted.current) {
        setIsFetchingReport(true);
      }
      reportRequestSequence.current += 1;
      const requestSequence = reportRequestSequence.current;

      try {
        const [forecastReport, thisNumberExplained] = await Promise.all([
          api.fetchForecastReportById(
            workspaceId,
            organisation.id,
            forecastId,
            generateQueryParams(reportData.periodType, newPeriodType)
          ),
          api.fetchForecastThisNumberExplained(workspaceId, organisation.id, forecastId)
        ]);

        if (isMounted.current && requestSequence === reportRequestSequence.current) {
          setReportData({
            periodType,
            forecastReportV2: forecastReport,
            thisNumberExplained: thisNumberExplained?.rows || {}
          });
        }
      } catch {
        if (
          !(isSyncStarting || isSyncInProgress) &&
          isMounted.current &&
          requestSequence === reportRequestSequence.current
        ) {
          addErrorMessage?.(new Error('Failed to fetch report'));
        }
      } finally {
        if (isMounted.current && requestSequence === reportRequestSequence.current) {
          setIsFetchingReport(false);
          setSettledForecastId(forecastId);
        }
      }
    },
    [addErrorMessage, forecast, isMounted, organisation, reportData.periodType, workspaceId]
  );

  const setPeriodType = useCallback(
    async (periodType: PeriodType) => {
      trackPromise(fetchForecastReport(undefined, periodType), LOADER_AREAS.predictArea);
      detailSettingsPersistor.persist({ periodType });
    },
    [fetchForecastReport]
  );

  /**
   * Temporarily synchronizes the locally cached forecast with a successful PATCH response.
   * Remove this bridge once DetailContextProvider uses React Query for forecast details.
   */
  const syncForecast = useCallback((updatedForecast: Forecast) => {
    setForecast(currentForecast =>
      currentForecast?.id === updatedForecast.id ? updatedForecast : currentForecast
    );
  }, []);

  const updateForecast = useCallback(
    async (
      update: Partial<Forecast> | Partial<ConsolidatedForecastUpdateRequest>,
      shouldRefetchReport = false,
      loadingArea = LOADER_AREAS.predictArea
    ): Promise<void> => {
      if (!forecast || !organisation) {
        return;
      }

      try {
        const updatedForecast = await api.updateForecast(
          update as Partial<Forecast>,
          workspaceId,
          organisation.id,
          forecast.id,
          undefined,
          loadingArea
        );

        if (isMounted.current) {
          setForecast(updatedForecast);
        }

        clearForecastsQueryCache(organisation.id, workspaceId);

        if (shouldRefetchReport) {
          fetchForecastReport();
        }
      } catch {
        addErrorMessage?.(new Error('Failed to update forecast'));
      }
    },
    [addErrorMessage, fetchForecastReport, forecast, isMounted, organisation, workspaceId]
  );

  // ***********
  // * Effects *
  // ***********

  // Load the forecast data on mount / route change
  useEffect(() => {
    if (
      isForecastDetailsRoute &&
      forecastIdFromURL !== lastLoadedForecastId.current &&
      forecastIdFromURL
    ) {
      // Keep stale data out of the new route and invalidate any report request from the previous forecast.
      reportRequestSequence.current += 1;
      setIsFetchingReport(false);
      setForecast(null);
      setReportData(currentReportData => ({
        forecastReportV2: null,
        periodType: currentReportData.periodType,
        thisNumberExplained: {}
      }));
      fetchForecast();
    }
  }, [fetchForecast, forecastIdFromURL, isForecastDetailsRoute]);

  // Load the forecast report data when the forecast ID changes
  useEffect(() => {
    if (forecast?.id) {
      fetchForecastReport();
    }
  }, [fetchForecastReport, forecast?.id]);

  const value = {
    ...reportData,
    deleteForecast,
    fetchForecast,
    fetchForecastReport: debounce(fetchForecastReport, 100) as IDetailContext['fetchForecastReport'],
    forecast,
    forecastGraphDate,
    isFetchingReport,
    isInitialDataLoading,
    setPeriodType,
    syncForecast,
    totalCashPredictRow,
    totalPastDates,
    updateForecast
  };

  return <DetailContext.Provider value={value}>{children}</DetailContext.Provider>;
};

export const useDetailContext = () => useContext(DetailContext);

export type DetailContextKeys = keyof IDetailContext;
const DEFAULT_OPTIONS: DetailContextKeys[] = [
  'fetchForecast',
  'fetchForecastReport',
  'forecast',
  'forecastReportV2',
  'updateForecast',
  'totalPastDates',
  'isFetchingReport',
  'periodType',
  'setPeriodType'
];

/**
 * Injects the ProductManagement context to the component passed as param.
 * @name withForecastDetailContext
 * @function
 * @param {React.ComponentType<RC & CP>} Component - Component to wrap with the consumer.
 * @param {Array<keyof C>} options - Context props to inject into the component.
 * @template {RC} - React component props without context props.
 * @template {CP} - Context props of the component.
 *
 * @returns {React.ComponentType<RC>} - Wrapped component.
 */
export const withDetailContext = getWithContextHOC(DetailContext, DEFAULT_OPTIONS);
